import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { NavBar } from './NavBar'

interface AppShellProps {
  children: React.ReactNode
}

export function AppShell({ children }: AppShellProps) {
  const wakeLockRef = useRef<WakeLockSentinel | null>(null)
  const mainRef = useRef<HTMLElement | null>(null)
  const location = useLocation()

  // Reset scroll on every route change so pages don't inherit each other's scroll position
  useEffect(() => {
    if (mainRef.current) mainRef.current.scrollTop = 0
  }, [location.pathname])

  useEffect(() => {
    if (!('wakeLock' in navigator)) return

    const acquire = async () => {
      try {
        wakeLockRef.current = await navigator.wakeLock.request('screen')
      } catch {
        // Permission denied or not supported — screen may still dim
      }
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') acquire()
    }

    acquire()
    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      wakeLockRef.current?.release()
    }
  }, [])

  return (
    <div className="h-screen overflow-hidden flex flex-col bg-gray-50 dark:bg-gray-950">
      {/* Safe area for notched devices */}
      <style>
        {`
          .safe-bottom {
            padding-bottom: max(0px, env(safe-area-inset-bottom));
          }
        `}
      </style>

      {/* Main content — takes remaining space above navbar */}
      <main ref={mainRef} className="flex-1 w-full overflow-hidden min-h-0">
        {children}
      </main>

      {/* Navigation bar */}
      <NavBar />
    </div>
  )
}
